import { PaymentProcessorEnum, Tooltip, getPaymentProcessorTitle, toast } from '@systemeio/ui'
import { Integration } from 'modules/profile/payment-gateways/components/integration'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { cashOnDeliveryDisconnect } from 'modules/profile/payment-gateways/integrations/cash-on-delivery/api'
import CashOnDeliveryCreateModal from 'modules/profile/payment-gateways/integrations/cash-on-delivery/components/cash-on-delivery-create-modal'
import CashOnDeliveryEditModal from 'modules/profile/payment-gateways/integrations/cash-on-delivery/components/cash-on-delivery-edit-modal'
import { CashOnDeliveryPaymentIntegrationInterface } from 'modules/profile/payment-gateways/integrations/cash-on-delivery/types'
import { getCashOnDeliveryHelpPageLink } from 'modules/profile/payment-gateways/integrations/cash-on-delivery/utils'
import React, { FC, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { BinIcon } from '@systemeio/ui/icons'

export const CashOnDelivery: FC = () => {
  const { user } = useUser()
  const { t } = useLocoTranslation()
  const { getConnectedPaymentIntegration, removePaymentIntegration } =
    useConnectedPaymentIntegrations()

  const [deleteOpened, setDeleteOpened] = useState(false)

  const cashOnDeliveryIntegration =
    getConnectedPaymentIntegration<CashOnDeliveryPaymentIntegrationInterface>({
      processor: PaymentProcessorEnum.CashOnDelivery,
    })

  const onDelete = async () => {
    await cashOnDeliveryDisconnect()
    await removePaymentIntegration({ processor: PaymentProcessorEnum.CashOnDelivery })
    toast.success(
      t('dashboard.settings.payment-gateways.cash_on_delivery.remove_toast_notification'),
    )
  }

  return (
    <Integration
      logo={
        <span className="whitespace-nowrap text-2xl font-bold text-black">
          {getPaymentProcessorTitle(PaymentProcessorEnum.CashOnDelivery)}
        </span>
      }
      additionalInfo={
        <a
          className="primary-link"
          href={getCashOnDeliveryHelpPageLink(user?.dashboardLocale)}
          target="_blank"
          rel="noreferrer"
        >
          <TransLoco
            t={t}
            i18nKey="dashboard.settings.payment_gateways.help_article_label"
            values={{
              integration: getPaymentProcessorTitle(PaymentProcessorEnum.CashOnDelivery),
            }}
          />
        </a>
      }
    >
      {cashOnDeliveryIntegration ? (
        <div className={'flex w-full justify-between gap-3 overflow-hidden'}>
          <div className={'flex flex-col gap-1 truncate'}>
            {!cashOnDeliveryIntegration.readyToUse && (
              <p className={'flex gap-1'}>
                {t('dashboard.settings.payment-gateways.integration_in_progress')}
              </p>
            )}
            <CashOnDeliveryEditModal
              instructions={cashOnDeliveryIntegration.details.instructions}
            />
          </div>
          <Tooltip
            mode={'hover'}
            label={t('dashboard.settings.payment-gateways.remove_integration', {
              payment: t(
                'dashboard.settings.payment-gateways.cash-on-delivery-integration',
              ).toLowerCase(),
            })}
            wrapperClassName={'flex items-center'}
            className={'h-fit'}
          >
            <button className={'group outline-hidden'} onClick={() => setDeleteOpened(true)}>
              <BinIcon
                className={
                  'group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]'
                }
              />
            </button>
          </Tooltip>
          <ConfirmationModal
            title={t('dashboard.settings.payment-gateways.remove_integration_description', {
              payment: t(
                'dashboard.settings.payment-gateways.cash-on-delivery-integration',
              ).toLowerCase(),
            })}
            confirmationText={t(
              'dashboard.settings.payment-gateways.remove_payment_text.cash_on_delivery',
            )}
            opened={deleteOpened}
            onClose={() => setDeleteOpened(false)}
            onConfirm={onDelete}
          />
        </div>
      ) : (
        <CashOnDeliveryCreateModal />
      )}
    </Integration>
  )
}
